import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

export const NetworkSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  return (
    <section id="network" ref={containerRef} className="py-24 bg-white relative overflow-hidden">
      {/* Background Map Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <img 
          src="https://public.youware.com/users-website-assets/prod/5cdbe780-871f-42f1-8d67-aecb910eea07/dfcf388866d848ef87a5b9b21f91d7bd.jpg" 
          alt="World Map" 
          className="w-full h-full object-cover grayscale"
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          <motion.div 
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h4 className="text-amber-500 font-bold uppercase tracking-widest text-sm mb-2">Global Reach</h4>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6 leading-tight">
              Connecting Clients with <br/>
              <span className="text-zinc-500">Opportunities Worldwide</span>
            </h2>
            <p className="text-zinc-600 text-lg leading-relaxed mb-8">
              Our curated global network spans Asia, the Americas, Europe, and the Middle East, connecting clients with industry specialists, investors, institutional stakeholders, high-level operators, commodity producers, off-takers, traders, and end-users.
            </p>
            <p className="text-zinc-600 text-lg leading-relaxed">
              This international reach enables us to structure transactions that traditional channels often cannot accommodate, bridging gaps with bespoke, commercially grounded solutions.
            </p>
          </motion.div>

          <motion.div 
            className="w-full lg:w-1/2"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="grid grid-cols-2 gap-4">
              {['Asia', 'Americas', 'Europe', 'Middle East'].map((region, index) => (
                <div key={index} className="bg-zinc-50 p-6 rounded-sm border border-zinc-100 text-center hover:shadow-md transition-shadow duration-300">
                  <div className="text-amber-500 font-bold text-xl mb-2">{region}</div>
                  <div className="text-zinc-400 text-xs uppercase tracking-wider">Active Operations</div>
                </div>
              ))}
            </div>
            <div className="mt-8 bg-zinc-900 text-white p-8 rounded-sm shadow-xl">
              <h3 className="text-xl font-bold mb-4">Direct Access To:</h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-zinc-300">
                <li className="flex items-center"><span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>Institutional Stakeholders</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>Industry Specialists</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>Commodity Producers</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>High-Level Operators</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>Traders & Off-takers</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>Private Investors</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};